// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.

export type GlobalMap = Record<string, unknown>;

/**
 * Apply key-value pairs to globalThis and return a restore function.
 * The original property descriptors are preserved and restored later.
 */
export function applyGlobals(globals: GlobalMap): () => void {
  const keys = Object.keys(globals);
  if (keys.length === 0) return () => {};

  const prev = new Map<string, PropertyDescriptor | undefined>();
  const hadOwn = new Map<string, boolean>();

  for (const key of keys) {
    // Save original descriptor
    const hasOwn = Object.prototype.hasOwnProperty.call(globalThis, key);
    hadOwn.set(key, hasOwn);
    prev.set(key, hasOwn ? Object.getOwnPropertyDescriptor(globalThis, key) : undefined);

    // Define with minimal, restorable attributes
    Object.defineProperty(globalThis as any, key, {
      value: globals[key],
      configurable: true, // allow restoration
      enumerable: false,  // keep globals unobtrusive by default
      writable: true,     // allow WASM or consumers to mutate if needed
    });
  }

  // Restore closure
  return () => {
    for (const key of keys) {
      const existed = hadOwn.get(key);
      const desc = prev.get(key);
      try {
        if (existed && desc) {
          Object.defineProperty(globalThis as any, key, desc);
        } else {
          delete (globalThis as any)[key];
        }
      } catch {
        // Fallback removal if restoration fails
        try { delete (globalThis as any)[key]; } catch {}
      }
    }
  };
}
